/**
 * editarSecciones.js — la parte pura de Editar campaña (rediseño, Fase 2). Sin React ni red.
 */

/** Las secciones, en el orden de la navegación lateral. */
// Revisión de la Fase 2: «Información» y «Visible para el usuario» van juntas (dos columnas) y los
// recordatorios pasan de «Fechas y horario» a «Reglas».
export const SECCIONES_EDITAR = [
  { id: 'informacion', titulo: 'Información', desc: 'Por dentro, a la izquierda; lo que ve la persona, a la derecha.' },
  { id: 'subtareas', titulo: 'Subtareas', desc: 'Lo que el técnico marca en cada cita.' },
  { id: 'reglas', titulo: 'Reglas', desc: 'Asignación de técnico, cancelaciones y recordatorios.' },
  { id: 'fechas', titulo: 'Fechas y horario', desc: 'Cuándo se puede reservar y cuánto dura cada cita.' },
  { id: 'dias', titulo: 'Días', desc: 'Fines de semana y días excluidos.' },
  { id: 'tecnicos', titulo: 'Técnicos', desc: 'Quién atiende las citas.' },
  { id: 'personas', titulo: 'Personas en lotes', desc: 'Se gestionan en el resumen de la campaña, pestaña Personas.' },
];

const SECCION_DE_CAMPO = {
  titulo: 'informacion', descripcion: 'informacion', ambito: 'informacion',
  titulo_usuario: 'informacion', descripcion_usuario: 'informacion',
  asignacion_modo: 'reglas', max_cancelaciones_default: 'reglas', horas_antelacion_cancelar: 'reglas',
  recordatorio_1_dias_lab_antes: 'reglas', recordatorio_2_dias_lab_antes: 'reglas',
  subtareas: 'subtareas',
  fecha_inicio: 'fechas', fecha_limite: 'fechas', horario_inicio: 'fechas', horario_fin: 'fechas',
  duracion_cita: 'fechas', franjas: 'fechas',
  excluidos_manuales: 'dias', permite_fin_semana: 'dias', excluir_fines_de_semana: 'dias',
  tecnicos_ids: 'tecnicos',
  usuarios_ids: 'personas',
};

/**
 * La sección de un campo con error del servidor. Los de subtareas pueden venir como
 * «subtareas[2].nombre» o «subtareas.2.nombre»: se mira el nombre antes del primer «[» o «.».
 */
export function seccionDeCampo(campo) {
  if (!campo) return null;
  const base = String(campo).split(/[.[]/)[0];
  return SECCION_DE_CAMPO[base] || null;
}

/** { seccion: nº de errores } con los errores del servidor ([{ campo, mensaje }]). */
export function erroresPorSeccion(errores) {
  const out = {};
  (Array.isArray(errores) ? errores : []).forEach((e) => {
    const s = seccionDeCampo(e?.campo);
    if (s) out[s] = (out[s] || 0) + 1;
  });
  return out;
}

/** Los errores que no caen en ninguna sección (se enseñan en la barra de abajo). */
export function erroresSinSeccion(errores) {
  return (Array.isArray(errores) ? errores : []).filter((e) => !seccionDeCampo(e?.campo));
}

/**
 * Huella de lo que se guardaría: el payload sin las cachés de nombres (no se envían). Dos huellas
 * iguales = nada que guardar. El reducer construye siempre los objetos en el mismo orden de claves.
 */
export function firmaPayload(payload) {
  if (!payload) return '';
  const { _tecnicos_cache, _usuarios_cache, ...resto } = payload;
  return JSON.stringify(resto);
}

export function hayCambios(payload, firmaBase) {
  if (firmaBase == null) return false;
  return firmaPayload(payload) !== firmaBase;
}

/** Transiciones de estado que ofrece Editar (la pantalla solo abre Activa y Pausada). */
export const TRANSICIONES_EDITAR = {
  Activa: [
    { estado: 'Pausada', label: 'Pausar' },
    { estado: 'Completada', label: 'Completar' },
    { estado: 'Cancelada', label: 'Cancelar campaña', peligro: true },
  ],
  Pausada: [
    { estado: 'Activa', label: 'Reactivar' },
    { estado: 'Cancelada', label: 'Cancelar campaña', peligro: true },
  ],
};

export function transicionesDe(estado) {
  return TRANSICIONES_EDITAR[estado] || [];
}

/**
 * ¿Hay que preguntar antes de seguir este clic? Solo con cambios sin guardar y si el clic navega a
 * OTRA página de la intranet en la misma pestaña: no con Ctrl/Cmd/Shift/Alt, botón central, target
 * distinto de _self, descargas, enlaces a otro origen ni anclas de la misma página.
 *
 * @param {object} a
 * @param {boolean} a.sucio
 * @param {string|null} a.href     href ya resuelto (absoluto) del <a>
 * @param {string} a.origen        window.location.origin
 * @param {string} a.rutaActual    pathname + search actuales
 * @param {string} [a.target]
 * @param {boolean} [a.descarga]
 * @param {number} [a.boton]       MouseEvent.button
 * @param {boolean} [a.modificador]
 */
export function debeAvisarAlSalir({ sucio, href, origen, rutaActual, target = '', descarga = false, boton = 0, modificador = false }) {
  if (!sucio || !href) return false;
  if (boton !== 0 || modificador || descarga) return false;
  if (target && target !== '_self') return false;
  let url;
  try { url = new URL(href, origen); } catch (e) { return false; }
  if (url.origin !== origen) return false;
  if (`${url.pathname}${url.search}` === rutaActual) return false; // misma página (o solo #ancla)
  return true;
}

/**
 * Lo que verá la persona en Mis Citas y en los avisos: el título y la descripción para el usuario, o
 * los internos si están vacíos. La MISMA regla que el servidor (resolverContenidoPublico en
 * contenidoService.js): el público, recortado, o si queda vacío el interno tal cual.
 */
export function contenidoParaUsuario(payload) {
  const tituloPub = String(payload?.titulo_usuario ?? '').trim();
  const descPub = String(payload?.descripcion_usuario ?? '').trim();
  return {
    titulo: tituloPub || payload?.titulo || '',
    descripcion: descPub || payload?.descripcion || '',
    tituloEsInterno: !tituloPub,
    descripcionEsInterna: !descPub,
  };
}

export const TEXTO_SALIR_SIN_GUARDAR = 'Hay cambios sin guardar en la campaña. ¿Salir sin guardarlos?';
